import { Autocomplete, Chip, paletteColors, Stack, TextField } from '@evaneos/b2b-ui';
import { DateCalendar } from '@evaneos/b2b-ui/date-picker';
import CalendarIcon from '@evaneos/b2b-ui/icons/CalendarToday';
import { useIntl } from 'react-intl';
import { SyntheticEvent, useRef, useState } from 'react';
import { formatISO, isEqual } from 'date-fns';

import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { DossierId, Task } from '../../../../resources/dossier/types';
import {
    useGetDossierData,
    useGetTaskOptionsByDossierId,
} from '../../../../resources/dossier/queries';
import {
    useChangeStepStatus,
    useChangeTaskDueDateMutation,
    useChangeTaskMutation,
    useChangeTaskNoteMutation,
} from '../../../../resources/dossier/mutations';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { StepName } from '../../../../design-system/types';

import { TaskCommentSection } from './TaskCommentSection';

type TaskOption = {
    label: string;
    value: string;
};

type TaskAndDueDateEditableItemProps = {
    dossierId: DossierId;
    task?: Task;
};

/* eslint-disable max-lines-per-function -- task/due-date editor with multi-mutation orchestration and tracking */
export const TaskAndDueDateEditableItem = ({
    dossierId,
    task,
}: TaskAndDueDateEditableItemProps) => {
    const { formatDate } = useIntl();
    const { definition: taskDefinition, dueDate, note } = task || {};
    const { currentTaskValue, taskOptions } = useGetTaskOptionsByDossierId(dossierId) || {};
    const { suggestedTask } = useGetDossierData(dossierId) || {};
    const changeTaskMutation = useChangeTaskMutation(dossierId);
    const changeTaskNoteMutation = useChangeTaskNoteMutation(dossierId);
    const changeDueDateMutation = useChangeTaskDueDateMutation(dossierId);
    const changeStepMutationStatus = useChangeStepStatus(dossierId);
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const [selectedTask, setSelectedTask] = useState<TaskOption | null>(null);
    const [commentText, setCommentText] = useState<string>(note ?? '');
    const selectedTaskRef = useRef<TaskOption | null>(null);
    const hasSubmittedRef = useRef(false);

    // eslint-disable-next-line sonarjs/cognitive-complexity -- complex logic, refactor deferred
    const submitTaskChange = () => {
        if (hasSubmittedRef.current) return;

        const newTask = selectedTaskRef.current;
        const taskHasChanged = newTask !== null && newTask.value !== taskDefinition;
        const noteHasChanged = commentText.trim() !== (note ?? '');
        const taskNote = commentText.trim() ? commentText.trim() : undefined;

        const onlyNoteHasChanged = !taskHasChanged && noteHasChanged;
        const nothingHasChanged = !taskHasChanged && !noteHasChanged;

        if (nothingHasChanged) return;

        if (onlyNoteHasChanged && !changeTaskNoteMutation.isPending) {
            if (!taskDefinition) return;
            hasSubmittedRef.current = true;

            changeTaskNoteMutation.mutate({ note: taskNote ?? '' });

            if (taskNote) {
                const nbaStep = taskDefinition.split('.')[0] as StepName;
                trackEvent((ampli, defaultProperties) => {
                    ampli.nbaNoteSubmitted({
                        ...defaultProperties,
                        ...trackedTripProperties,
                        nba_name: taskDefinition,
                        nba_step: nbaStep,
                    });
                });
            }
            return;
        }

        const canSubmitTaskChange = taskHasChanged && !changeTaskMutation.isPending;

        if (canSubmitTaskChange) {
            hasSubmittedRef.current = true;
            const newTaskDefinition = newTask?.value ?? taskDefinition;
            const nbaStep = newTaskDefinition.split('.')[0] as StepName;

            changeTaskMutation.mutate({
                taskDefinition: newTaskDefinition,
                taskNote,
            });

            trackEvent((ampli, defaultProperties) => {
                ampli.nextBestActionSubmitted({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    has_comment: !!taskNote,
                    nba_name: newTaskDefinition,
                    nba_step: nbaStep,
                    next_best_action_suggested_clicked: suggestedTask === newTaskDefinition,
                });
            });

            if (noteHasChanged && !!taskNote) {
                trackEvent((ampli, defaultProperties) => {
                    ampli.nbaNoteSubmitted({
                        ...defaultProperties,
                        ...trackedTripProperties,
                        nba_name: newTaskDefinition,
                        nba_step: nbaStep,
                    });
                });
            }
        }
    };

    const resetEditorState = () => {
        setSelectedTask(null);
        selectedTaskRef.current = null;
        hasSubmittedRef.current = false;
    };

    const handleTaskChange = (
        _event: SyntheticEvent,
        newValue: TaskOption | null,
        close: () => void,
    ) => {
        setSelectedTask(newValue);
        selectedTaskRef.current = newValue;

        if (!commentText) {
            close();
        }
    };

    const handleDueDateChange = (newDate: Date | null, close: () => void) => {
        if (!newDate) return;

        if (dueDate && !isEqual(dueDate, newDate) && !changeDueDateMutation.isPending) {
            const formattedDate = formatISO(newDate);
            changeDueDateMutation.mutate({ dueDate: formattedDate });

            trackEvent((ampli, defaultProperties) => {
                ampli.dueDateSubmitted({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }
        close();
    };

    const isEditingDisabled =
        changeTaskMutation.isPending ||
        changeTaskNoteMutation.isPending ||
        changeDueDateMutation.isPending ||
        changeStepMutationStatus.isPending;

    return (
        <DefinitionList.EditableGroup
            labelId="next-action"
            disabled={isEditingDisabled}
            items={[
                {
                    id: 'task-editor',
                    popoverWidth: 'trigger',
                    placement: 'over',
                    disablePopoverMinHeight: true,
                    disabled: !taskOptions || taskOptions.length === 0,
                    trigger: <TaskChip taskValue={currentTaskValue} />,
                    onBeforeClose: () => {
                        submitTaskChange();
                        resetEditorState();
                    },
                    editor: ({ close }) => (
                        <Stack>
                            <form>
                                <Autocomplete
                                    onChange={(_event, newValue) =>
                                        handleTaskChange(_event, newValue, close)
                                    }
                                    disablePortal
                                    autoHighlight
                                    autoComplete
                                    options={taskOptions ?? []}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                    }
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            background: paletteColors.neutral[100],
                                        },
                                        '& .MuiInputBase-input': { fontSize: '1.4rem' },
                                        '& .MuiAutocomplete-popper': {
                                            position: 'relative !important',
                                            transform: 'none !important',
                                        },
                                    }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                '& .MuiAutocomplete-option': { fontSize: '1.4rem' },
                                                boxShadow: 'none',
                                                border: `1px solid ${paletteColors.neutral[300]}`,
                                                borderTop: 'none',
                                                borderRadius: 0,
                                                marginTop: 0,
                                            },
                                        },
                                        popper: {
                                            sx: {
                                                position: 'relative !important',
                                                transform: 'none !important',
                                            },
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            sx={{ fontSize: '1.4rem' }}
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <TaskChip
                                                            taskValue={
                                                                selectedTask ?? currentTaskValue
                                                            }
                                                        />
                                                    ),
                                                },
                                                htmlInput: {
                                                    ...params.inputProps,
                                                    readOnly: true,
                                                    style: {
                                                        width: 0,
                                                        minWidth: 0,
                                                        padding: 0,
                                                        opacity: 0,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Stack
                                            component="li"
                                            height="40px"
                                            direction="row"
                                            {...props}
                                            key={option.value}
                                        >
                                            <TaskChip taskValue={option} />
                                        </Stack>
                                    )}
                                    disableClearable
                                    size="small"
                                    open
                                    popupIcon={null}
                                />
                            </form>
                            <TaskCommentSection
                                comment={commentText}
                                onCommentChange={(value: string) => setCommentText(value)}
                            />
                        </Stack>
                    ),
                },
                {
                    id: 'due-date-editor',
                    popoverWidth: 'fit-content',
                    trigger: (
                        <Chip
                            data-testid="due-date-chip"
                            component="strong"
                            label={formatDate(dueDate, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            })}
                            icon={
                                <CalendarIcon
                                    aria-hidden="true"
                                    sx={{ fontSize: '16px !important' }}
                                />
                            }
                            size="small"
                            variant="filled"
                            sx={{
                                fontWeight: 'normal',
                                backgroundColor: paletteColors.yellow[100],
                            }}
                        />
                    ),
                    editor: ({ close }) => (
                        <DateCalendar
                            value={dueDate ?? new Date()}
                            onChange={(newDate) => handleDueDateChange(newDate, close)}
                            minDate={new Date()}
                            views={['day']}
                        />
                    ),
                },
            ]}
        />
    );
};
/* eslint-enable max-lines-per-function */

const TaskChip = ({ taskValue }: { taskValue: TaskOption | undefined }) => {
    return (
        <Chip
            label={taskValue?.label}
            size="small"
            variant="filled"
            component="strong"
            sx={{
                fontWeight: 'normal',
                backgroundColor: paletteColors.yellow[100],
            }}
        />
    );
};
